import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import LoadingSpinner from "../LoadingSpinner";
import OrderCard from "./OrderCard";
import { Box, Typography } from "@mui/material"; // Import Box and Typography for layout/messages

function AllOrders() {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(''); // <--- NEW: Search term state
  const { token } = useSelector((state) => state.auth);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await fetch(
          "https://test-api.stainedglassusa.com/orders",
          {
            headers: {
              Authorization: `bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Fetched orders data:", data);
        setOrders(data);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [token]);

  // <--- NEW: Filtering and Sorting Logic ---
  const filteredAndSortedOrders = useMemo(() => {
    // 1. Sort by order.id (or any other default sort criteria)
    const sortedOrders = [...orders].sort((a, b) => a.id - b.id);

    // 2. Filter based on searchTerm
    const filteredOrders = sortedOrders.filter(order => {
      if (!order) return false; // Basic check for valid order object

      const lowerCaseSearchTerm = searchTerm.toLowerCase();

      // Search by Order ID
      const idMatch = String(order.id).includes(lowerCaseSearchTerm);
      // Search by User Email
      const emailMatch = order.email?.toLowerCase().includes(lowerCaseSearchTerm);
      // Search by Status
      const statusMatch = order.status?.toLowerCase().includes(lowerCaseSearchTerm);
      // Search by User Image ID (if applicable)
      const userImageIdMatch = order.user_image_id ? String(order.user_image_id).includes(lowerCaseSearchTerm) : false;

      // Combine all search criteria
      return idMatch || emailMatch || statusMatch || userImageIdMatch;
    });

    return filteredOrders;
  }, [orders, searchTerm]); // Dependencies for memoization

  // <--- NEW: Search input handler ---
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };
  // <--- END NEW ---

  return (
    <Box sx={{ padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
        All Quotes
      </Typography>

      {/* <--- NEW: Search Bar --- */}
      <Box sx={{ mb: 3, width: '100%', maxWidth: '600px' }}>
        <input
          id="order-search-input"
          type="text"
          placeholder="Search by Order ID, User Email, Status, or User Image ID"
          value={searchTerm}
          onChange={handleSearchChange}
          style={{
            width: '100%',
            padding: '12px 15px',
            fontSize: '1rem',
            borderRadius: '8px',
            border: '1px solid #ccc',
            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
            transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
            '&:focus': {
              borderColor: '#66afe9',
              boxShadow: '0 0 8px rgba(102,175,233,0.6)'
            }
          }}
        />
      </Box>
      {/* <--- END NEW --- */}

      {isLoading ? (
        <LoadingSpinner />
      ) : filteredAndSortedOrders.length === 0 ? ( // Use filteredAndSortedOrders here
        <Typography variant="h6" color="text.secondary">
          No quotes found matching your criteria.
        </Typography>
            ) : (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '20px',
            width: '100%',
            maxWidth: '1600px',
            margin: '0 auto'
          }}
        >
          {filteredAndSortedOrders.slice().reverse().map((order) => ( // Reverse order for initial state
            <OrderCard key={order.id} order={order} />
          ))}
        </Box>
      )}
    </Box>
  );
}

export default AllOrders;